import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, ListGroup, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { api } from "./api";

const EditOrder = ({ order, onClose, onSaved }) => {
  const [details, setDetails] = useState(null);
  const [availableItems, setAvailableItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState("1");
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const loadDetails = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [orderResponse, itemsResponse] = await Promise.all([
        api.get(`/api/order/getbyid/${order.id}`),
        api.get("/api/order/items"),
      ]);
      setDetails(orderResponse.data);
      setStatus(orderResponse.data.status);
      setAvailableItems(itemsResponse.data);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Unable to load order details.");
    } finally {
      setLoading(false);
    }
  }, [order.id]);

  useEffect(() => { loadDetails(); }, [loadDetails]);

  const results = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return [];
    return availableItems.filter((item) => item.name.toLowerCase().includes(query)).slice(0, 10);
  }, [availableItems, search]);

  const addItem = async () => {
    const parsedQuantity = Number(quantity);
    if (!selectedItem) return toast.warning("Select an item.");
    if (!Number.isInteger(parsedQuantity) || parsedQuantity < 1 || parsedQuantity > 10000) return toast.warning("Quantity must be between 1 and 10,000.");
    if (details.items.some((item) => item.item_id === selectedItem.item_id)) return toast.warning("This item is already included.");

    setBusyAction("add");
    try {
      await api.post(`/api/order/add-item/${order.id}`, { item_id: selectedItem.item_id, quantity: parsedQuantity });
      toast.success("Item added.");
      setSearch(""); setSelectedItem(null); setQuantity("1");
      await loadDetails();
      await onSaved();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to add item.");
    } finally { setBusyAction(""); }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Remove ${item.name} from this order?`)) return;
    setBusyAction(`remove-${item.item_id}`);
    try {
      await api.delete(`/api/order/delete/${order.id}/remove-item/${item.item_id}`);
      toast.success("Item removed.");
      await loadDetails();
      await onSaved();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to remove item.");
    } finally { setBusyAction(""); }
  };

  const updateStatus = async () => {
    if (status === details.status) return toast.info("No status change to save.");
    if (status === "delivered" && !window.confirm("Mark this order as delivered? This cannot be reversed here.")) return;
    setBusyAction("status");
    try {
      await api.put(`/api/order/update-status/${order.id}`, { status });
      toast.success("Order updated.");
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update order.");
    } finally { setBusyAction(""); }
  };

  if (loading) return <div className="text-center p-4"><Spinner /><p className="text-muted mt-2">Loading order...</p></div>;
  if (errorMessage) return <Alert variant="danger">{errorMessage} <Button size="sm" variant="outline-danger" onClick={loadDetails}>Retry</Button></Alert>;

  const editable = details.status === "pending";
  return <>
    <p><strong>Customer:</strong> {details.customer.first_name} {details.customer.last_name}</p>
    {!editable && <Alert variant="info">Delivered orders are read-only.</Alert>}
    <h2 className="h6">Order items</h2>
    <ListGroup className="mb-3">{details.items.map((item) => <ListGroup.Item key={item.item_id} className="d-flex justify-content-between align-items-center"><span>{item.name} <strong>× {item.quantity}</strong></span><Button variant="outline-danger" size="sm" disabled={!editable || Boolean(busyAction)} onClick={() => removeItem(item)}>{busyAction === `remove-${item.item_id}` ? <Spinner size="sm" /> : "Remove"}</Button></ListGroup.Item>)}</ListGroup>
    {editable && <Row className="g-2 mb-4"><Col md={7}><Form.Group><Form.Label>Add item</Form.Label><Form.Control value={search} onChange={(event) => { setSearch(event.target.value); setSelectedItem(null); }} placeholder="Search item" autoComplete="off" /><ListGroup>{results.map((item) => <ListGroup.Item action key={item.item_id} active={selectedItem?.item_id === item.item_id} onClick={() => { setSelectedItem(item); setSearch(item.name); }}>{item.name}</ListGroup.Item>)}</ListGroup></Form.Group></Col><Col md={3}><Form.Group><Form.Label>Quantity</Form.Label><Form.Control type="number" min="1" max="10000" value={quantity} onChange={(event) => setQuantity(event.target.value)} /></Form.Group></Col><Col md={2} className="d-flex align-items-end"><Button className="w-100" variant="success" disabled={Boolean(busyAction)} onClick={addItem}>{busyAction === "add" ? <Spinner size="sm" /> : "Add"}</Button></Col></Row>}
    <Row className="align-items-end"><Col md={5}><Form.Group><Form.Label>Order status</Form.Label><Form.Select value={status} disabled={!editable || Boolean(busyAction)} onChange={(event) => setStatus(event.target.value)}><option value="pending">Pending</option><option value="delivered">Delivered</option></Form.Select></Form.Group></Col><Col md={7} className="mt-3 mt-md-0"><Button className="me-2" disabled={!editable || Boolean(busyAction)} onClick={updateStatus}>{busyAction === "status" ? <><Spinner size="sm" className="me-2" />Saving...</> : "Save status"}</Button><Button variant="secondary" disabled={Boolean(busyAction)} onClick={onClose}>Close</Button></Col></Row>
  </>;
};

export default EditOrder;
